import { formatPluginName } from '@reflex/ui/lib/format';
import { canOpenAdminConsole, type AdminConsoleAccess } from './hrefEntitlement.js';
import type { Notification } from '../shared/types.js';

/** The title/subtitle pair a given viewer should be shown for a row. */
export interface NotificationHeading {
  title: string;
  subtitle: string | null;
}

/**
 * The heading a specific reader should see for a notification.
 *
 * Almost every notification is written for one audience and the stored
 * `title`/`subtitle` are it. `plugin_disabled` is the exception: the server
 * writes an operator heading — `Plugin disabled: gitlab` over the raw
 * `PluginStatusRegistry` failure string ("Startup health check failed:
 * GITLAB_TOKEN rejected with 401") — because the row is addressed to a
 * super-admin id. A reader who cannot open the admin console can do nothing
 * with either string; the reason in particular is a server internal that
 * names secrets and infrastructure, and it should not be their first
 * impression of the product breaking.
 *
 * `PluginDisabledRenderer` already branches the *body* on the same
 * predicate. The heading has to branch with it, and in the same place for
 * every surface that shows one — the detail panel, the inbox row, and the
 * list-page row all render `notification.title` (the rows additionally hang
 * it off a `title` tooltip), so a per-surface copy of this rule would leak
 * the reason string from whichever surface was updated last.
 *
 * The question asked is about the *viewer*, never about the deployment:
 * a demoted admin, a tenant on a dedicated instance, and a member of a
 * second org on that same instance all get the safe heading, and the sole
 * operator of an auth-disabled self-host still gets the diagnosis.
 */
export function notificationHeadingFor(
  notification: Notification,
  viewer: AdminConsoleAccess,
): NotificationHeading {
  if (notification.payload.type === 'plugin_disabled' && !canOpenAdminConsole(viewer)) {
    return {
      title: `${formatPluginName(notification.payload.pluginName)} is unavailable`,
      // Deliberately dropped rather than replaced: the body already says
      // what the outage means for this reader, and a second line here
      // would only repeat it in the one spot where it cannot be branched
      // any further (the inbox row's tooltip).
      subtitle: null,
    };
  }
  return { title: notification.title, subtitle: notification.subtitle ?? null };
}
